import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AdminDrawer } from "@/components/admin/AdminDrawer";
import { useDialogFocus } from "@/hooks/useDialogFocus";
import type { BonPrecoItem } from "@/components/dashboard/BonPrecosCard";
import { DashboardDetailDrawer } from "@/components/dashboard/ledger/DashboardDetailDrawer";
import {
  DashboardRankingDrawer,
  type DashboardRankingRowModel,
  type DashboardRankingSortOption,
} from "@/components/dashboard/ledger/DashboardRankingDrawer";
import type { CurrentUser, UserRole } from "@/features/auth/currentUserTypes";
import {
  useDashboardV2Detail,
  useDashboardV2Ranking,
} from "@/features/dashboard/hooks";
import {
  formatDashboardCount,
  formatDashboardCurrency,
  formatDashboardPercent,
  formatDashboardUnitQuantity,
} from "@/features/dashboard/metricFormatters";
import type { DashboardPeriodState } from "@/features/dashboard/period";
import type {
  DashboardAnalyticsResponse,
  DashboardDetailKind,
  DashboardDetailResponse,
  DashboardRankingKind,
  DashboardRankingResponse,
  DashboardRankingSort,
  DashboardUnitQuantity,
} from "@/features/dashboard/types";
import type { CheckerDashboardPanelRequest } from "./CheckerDashboard";
import type { ClientDashboardPanelRequest } from "./ClientDashboard";
import type { OverviewerDashboardPanelRequest } from "./OverviewerDashboard";
import type { SuperDashboardPanelRequest } from "./SuperDashboard";
import type { SupplierDashboardPanelRequest } from "./SupplierDashboard";

export type DashboardCategoryPanelRequest = {
  mode: "category";
  categoryName?: string;
};

function SuperPanelFrame({
  open,
  title,
  onClose,
  onAfterClose,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  onAfterClose?: () => void;
  children: ReactNode;
  visualTreatment?: "dashboard";
}) {
  const dialog = useRef<HTMLDivElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  if (open && !opener.current && typeof document !== "undefined")
    opener.current = document.activeElement as HTMLElement;
  useDialogFocus(dialog, onClose, {
    enabled: open,
    initialFocusRef: closeButton,
    restoreTarget: opener.current,
  });
  useEffect(() => {
    if (!open) onAfterClose?.();
  }, [open, onAfterClose]);
  if (!open || typeof document === "undefined") return null;
  return createPortal(
    <div
      className="lnk209-super-dialog-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div
        className={`lnk209-super-dialog${title === "Lojas por compras" ? " lnk209-ranking-dialog" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        ref={dialog}
        tabIndex={-1}
      >
        <header>
          <h2>{title}</h2>
          <button
            ref={closeButton}
            aria-label="Fechar detalhes"
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="lnk209-super-dialog-content">{children}</div>
      </div>
    </div>,
    document.body,
  );
}

export type DashboardPanelRequest =
  | SuperDashboardPanelRequest
  | ClientDashboardPanelRequest
  | SupplierDashboardPanelRequest
  | CheckerDashboardPanelRequest
  | OverviewerDashboardPanelRequest
  | DashboardCategoryPanelRequest;

type RankingRequest = Extract<DashboardPanelRequest, { mode: "ranking" }>;
type DetailRequest = Extract<DashboardPanelRequest, { mode: "detail" }>;
type LocalRequest = Exclude<
  DashboardPanelRequest,
  RankingRequest | DetailRequest
>;

interface DashboardDrilldownHostProps {
  user: CurrentUser;
  period: DashboardPeriodState;
  analytics?: DashboardAnalyticsResponse;
  request: DashboardPanelRequest | null;
  open?: boolean;
  onRequestChange: (request: DashboardPanelRequest | null) => void;
  onAfterClose?: () => void;
}

const detailKindByRanking: Readonly<
  Record<DashboardRankingKind, Exclude<DashboardDetailKind, "load">>
> = {
  clients: "client",
  suppliers: "supplier",
  products: "product",
  stores: "store",
  checkers: "checker",
};

const rankingAllowed = (role: UserRole, request: RankingRequest): boolean => {
  const { kind, sort } = request;
  const validSort =
    kind === "suppliers"
      ? sort === "return_rate" ||
        sort === "financial_impact" ||
        (role === "super" && sort === "value")
      : kind === "checkers"
        ? sort === "checked_items"
        : sort === "value";
  if (!validSort) return false;

  switch (role) {
    case "super":
      return (
        kind === "clients" ||
        kind === "suppliers" ||
        kind === "products" ||
        kind === "stores"
      );
    case "client":
    case "client_store":
      return kind === "suppliers" || kind === "products" || kind === "stores";
    case "supplier":
      return kind === "products" || kind === "stores";
    case "overviewer":
      return kind === "checkers";
    case "checker":
    case "admin_helper":
      return false;
  }
};

const detailAllowed = (role: UserRole, request: DetailRequest): boolean => {
  switch (role) {
    case "super":
      return (
        request.kind === "client" ||
        request.kind === "supplier" ||
        request.kind === "product" ||
        request.kind === "store"
      );
    case "client":
    case "client_store":
      return (
        request.kind === "supplier" ||
        request.kind === "product" ||
        request.kind === "store"
      );
    case "supplier":
      return (
        request.kind === "product" ||
        request.kind === "store" ||
        request.kind === "load"
      );
    case "checker":
      return request.kind === "load";
    case "overviewer":
      return request.kind === "checker" || request.kind === "load";
    case "admin_helper":
      return false;
  }
};

const localAllowed = (role: UserRole, request: LocalRequest): boolean => {
  switch (request.mode) {
    case "category":
      return role === "super" || role === "client" || role === "client_store";
    case "price_drop":
      return role === "client" || role === "client_store";
    case "performance":
      return role === "checker";
    case "quantities":
    case "quality":
      return true;
  }
};

const requestAllowed = (
  role: UserRole,
  request: DashboardPanelRequest | null,
): boolean => {
  if (!request) return false;
  if (request.mode === "ranking") return rankingAllowed(role, request);
  if (request.mode === "detail") return detailAllowed(role, request);
  return localAllowed(role, request);
};

const formattedMetric = (
  label: string,
  formatted: { full: string; accessibleLabel: string },
) => ({
  label,
  value: formatted.full,
  accessibleLabel: `${label}: ${formatted.accessibleLabel}`,
});

const quantityMetrics = (quantities: readonly DashboardUnitQuantity[]) =>
  quantities.flatMap(({ unit, quantity }) => {
    const formatted = formatDashboardUnitQuantity(unit, quantity);
    return formatted ? [formattedMetric(unit, formatted)] : [];
  });

function rankingRows(
  pages: readonly DashboardRankingResponse[],
): DashboardRankingRowModel[] {
  return pages.flatMap((page) => {
    switch (page.kind) {
      case "clients":
      case "stores":
      case "products":
        return page.items.map((item) => ({
          id: item.id,
          name: item.name,
          metrics: [
            formattedMetric("Valor", formatDashboardCurrency(item.value_cents)),
            ...quantityMetrics(item.quantities),
          ],
        }));
      case "suppliers":
        if (page.sort === "value")
          return page.items.map((item) => ({
            id: item.id,
            name: item.name,
            metrics: [
              formattedMetric(
                "Valor",
                formatDashboardCurrency(item.value_cents),
              ),
            ],
          }));
        return page.items.map((item) => {
          const rate = formattedMetric(
            "Retorno",
            formatDashboardPercent(item.return_rate),
          );
          const impact = formattedMetric(
            "Impacto",
            formatDashboardCurrency(item.financial_impact_cents),
          );
          return {
            id: item.id,
            name: item.name,
            metrics:
              page.sort === "financial_impact"
                ? [impact, rate]
                : [rate, impact],
          };
        });
      case "checkers":
        return page.items.map((item) => ({
          id: item.checker_id,
          name: item.checker_name,
          metrics: [
            formattedMetric("Itens", formatDashboardCount(item.checked_items)),
            formattedMetric(
              "Cargas concluídas",
              formatDashboardCount(item.concluded_loads),
            ),
            formattedMetric(
              "Divergências",
              formatDashboardCount(item.divergences),
            ),
          ],
        }));
    }
  });
}

const rankingSortOptions = (
  kind: DashboardRankingKind,
  role: UserRole,
): readonly DashboardRankingSortOption[] =>
  kind === "suppliers"
    ? [
        ...(role === "super"
          ? [{ value: "value" as const, label: "Vendas" }]
          : []),
        { value: "return_rate", label: "Taxa de retorno" },
        { value: "financial_impact", label: "Impacto financeiro" },
      ]
    : kind === "checkers"
      ? [{ value: "checked_items", label: "Itens conferidos" }]
      : [{ value: "value", label: "Valor" }];

const rankingTitle = (
  kind: DashboardRankingKind,
  sort: DashboardRankingSort,
): string => {
  if (kind === "clients") return "Melhores clientes";
  if (kind === "products") return "Produtos em destaque";
  if (kind === "stores") return "Lojas";
  if (kind === "checkers") return "Comparativo por conferente";
  return sort === "value"
    ? "Fornecedores por vendas"
    : sort === "financial_impact"
      ? "Fornecedores por impacto financeiro"
      : "Fornecedores por taxa de retorno";
};

const localTitle = (role: UserRole, request: LocalRequest): string => {
  switch (request.mode) {
    case "category":
      return "Compras por categoria";
    case "quantities":
      return role === "supplier"
        ? "Quantidade vendida"
        : role === "checker" || role === "overviewer"
          ? "Quantidades conferidas"
          : "Quantidades compradas";
    case "quality":
      return role === "supplier"
        ? "Qualidade das entregas"
        : role === "checker" || role === "overviewer"
          ? "Resultados de divergências"
          : "Resultados de qualidade";
    case "performance":
      return "Meu desempenho";
    case "price_drop":
      return "Detalhes do bom preço";
  }
};

type LocalRow = { label: string; value: string };

const countRow = (label: string, value: number): LocalRow => ({
  label,
  value: formatDashboardCount(value).full,
});

function quantityRows(analytics?: DashboardAnalyticsResponse): LocalRow[] {
  if (!analytics) return [];
  const quantities =
    analytics.role === "super"
      ? analytics.sections.purchased_quantities
      : analytics.role === "client" || analytics.role === "client_store"
        ? analytics.sections.purchased_quantities
        : analytics.role === "supplier"
          ? analytics.sections.quantity_sold
          : analytics.metrics.checked_quantities.status === "available"
            ? analytics.metrics.checked_quantities.quantities
            : [];
  return quantities.flatMap(({ unit, quantity }) => {
    const value = formatDashboardUnitQuantity(unit, quantity);
    return value ? [{ label: unit.toUpperCase(), value: value.full }] : [];
  });
}

function qualityRows(analytics?: DashboardAnalyticsResponse): LocalRow[] {
  if (!analytics) return [];
  const outcomes =
    analytics.role === "supplier"
      ? analytics.sections.quality_outcomes
      : analytics.role === "checker" ||
          analytics.role === "overviewer" ||
          analytics.role === "admin_helper"
        ? analytics.sections.divergence_outcomes
        : analytics.sections.quality_outcomes;
  const rows = [
    countRow("Aguardando decisão", outcomes.decision_count),
    countRow("Substituições", outcomes.substituted_count),
    countRow("Devoluções", outcomes.returned_count),
  ];
  if ("divergence_count" in outcomes) {
    rows.push(countRow("Divergências", outcomes.divergence_count));
  }
  if ("return_impact_cents" in outcomes) {
    rows.push({
      label: "Impacto das devoluções",
      value: formatDashboardCurrency(outcomes.return_impact_cents).full,
    });
  }
  return rows;
}

function localRows(
  user: CurrentUser,
  analytics: DashboardAnalyticsResponse | undefined,
  request: LocalRequest,
): LocalRow[] {
  switch (request.mode) {
    case "category": {
      const categories =
        analytics?.role === "super" ||
        analytics?.role === "client" ||
        analytics?.role === "client_store"
          ? analytics.sections.category_spend
          : [];
      return categories
        .filter(
          (item) =>
            !request.categoryName ||
            item.category_name === request.categoryName,
        )
        .slice(0, 20)
        .map((item) => ({
          label: item.category_name,
          value: formatDashboardCurrency(item.value_cents).full,
        }));
    }
    case "quantities":
      return quantityRows(analytics);
    case "quality":
      return qualityRows(analytics);
    case "performance": {
      if (analytics?.role !== "checker") return [];
      const own = analytics.sections.checker_highlights.find(
        (item) => item.checker_id === user.id,
      );
      return own
        ? [
            countRow("Itens conferidos", own.checked_items),
            countRow("Cargas concluídas", own.concluded_loads),
            countRow("Divergências", own.divergences),
          ]
        : [
            analytics.metrics.checked_items.status === "available"
              ? countRow(
                  "Itens conferidos",
                  analytics.metrics.checked_items.count,
                )
              : null,
            analytics.metrics.checked_loads.status === "available"
              ? countRow(
                  "Cargas concluídas",
                  analytics.metrics.checked_loads.count,
                )
              : null,
            analytics.metrics.divergences.status === "available"
              ? countRow("Divergências", analytics.metrics.divergences.count)
              : null,
          ].filter((row): row is LocalRow => row !== null);
    }
    case "price_drop":
      return priceDropRows(request.item);
  }
}

function priceDropRows(item: BonPrecoItem): LocalRow[] {
  return [
    { label: "Produto", value: item.product_name },
    { label: "Fornecedor", value: item.supplier_name },
    {
      label: "Preço anterior",
      value: formatDashboardCurrency(item.previous_price_cents).full,
    },
    {
      label: "Preço atual",
      value: formatDashboardCurrency(item.current_price_cents).full,
    },
    {
      label: "Redução",
      value: formatDashboardPercent(String(item.percentage_drop) as `${number}`)
        .full,
    },
  ];
}

function DashboardLocalPanel({
  title,
  rows,
}: {
  title: string;
  rows: readonly LocalRow[];
}) {
  return (
    <section aria-label={title}>
      {rows.length > 0 ? (
        <dl style={{ display: "grid", gap: "0.75rem", margin: 0 }}>
          {rows.map((row, index) => (
            <div
              key={`${row.label}-${index}`}
              style={{
                borderBottom: "1px solid var(--login-border)",
                display: "grid",
                gap: "0.2rem",
                paddingBottom: "0.75rem",
              }}
            >
              <dt style={{ color: "var(--login-muted)", fontSize: "0.78rem" }}>
                {row.label}
              </dt>
              <dd style={{ color: "var(--login-text)", margin: 0 }}>
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <p role="status">Nenhum dado disponível neste período.</p>
      )}
    </section>
  );
}

export function DashboardDrilldownHost({
  user,
  period,
  analytics,
  request,
  open = true,
  onRequestChange,
  onAfterClose,
}: DashboardDrilldownHostProps) {
  const allowed = requestAllowed(user.role, request);
  const drawerOpen = open && allowed;
  const rankingRequest =
    allowed && request?.mode === "ranking" ? request : null;
  const detailRequest = allowed && request?.mode === "detail" ? request : null;
  const localRequest =
    allowed &&
    request &&
    request.mode !== "ranking" &&
    request.mode !== "detail"
      ? request
      : null;
  const ranking = useDashboardV2Ranking({
    user,
    isOpen: drawerOpen && rankingRequest !== null,
    period,
    kind: rankingRequest?.kind ?? "clients",
    sort: rankingRequest?.sort ?? "value",
  } as Parameters<typeof useDashboardV2Ranking>[0]);
  const detailKind = detailRequest?.kind ?? "load";
  const detail = useDashboardV2Detail(
    detailKind === "load"
      ? {
          user,
          isOpen: drawerOpen && detailRequest !== null,
          kind: "load",
          entityId: detailRequest?.id ?? "",
          period: null,
          sort: "created",
        }
      : {
          user,
          isOpen: drawerOpen && detailRequest !== null,
          kind: detailKind,
          entityId: detailRequest?.id ?? "",
          period,
          sort: "recent",
        },
  );
  const returnScrollTopRef = useRef(0);
  const [restoreFocusItemId, setRestoreFocusItemId] = useState<string | null>(
    null,
  );
  const rankingPages =
    (ranking.data?.pages as readonly DashboardRankingResponse[] | undefined) ??
    [];
  const rows = useMemo(() => rankingRows(rankingPages), [rankingPages]);
  const detailPages = detail.data?.pages as
    | readonly DashboardDetailResponse[]
    | undefined;
  const presentation =
    user.role === "checker" || user.role === "overviewer"
      ? ("operational" as const)
      : ("financial" as const);
  const title =
    rankingRequest?.title ??
    detailRequest?.title ??
    (localRequest
      ? localTitle(user.role, localRequest)
      : "Detalhes do dashboard");

  const PanelFrame =
    user.role === "super" ||
    user.role === "supplier" ||
    (user.role === "client" && rankingRequest?.kind === "stores")
      ? SuperPanelFrame
      : AdminDrawer;
  return (
    <PanelFrame
      open={drawerOpen}
      title={title}
      visualTreatment="dashboard"
      onClose={() => onRequestChange(null)}
      onAfterClose={onAfterClose}
    >
      {rankingRequest ? (
        <DashboardRankingDrawer
          title={rankingRequest.title}
          items={rows}
          sort={rankingRequest.sort}
          sortOptions={rankingSortOptions(rankingRequest.kind, user.role)}
          isPending={ranking.isPending}
          hasNextPage={Boolean(ranking.hasNextPage)}
          isFetchingNextPage={ranking.isFetchingNextPage}
          error={ranking.error ?? ranking.authorityError}
          restoreScrollTop={returnScrollTopRef.current}
          restoreFocusItemId={restoreFocusItemId}
          onSortChange={(sort: DashboardRankingSort) =>
            onRequestChange({
              ...rankingRequest,
              sort,
              title:
                user.role === "super" && rankingRequest.kind === "stores"
                  ? "Melhores lojas"
                  : rankingTitle(rankingRequest.kind, sort),
            } as RankingRequest)
          }
          onOpenDetail={(item, scrollTop) => {
            returnScrollTopRef.current = scrollTop;
            setRestoreFocusItemId(null);
            onRequestChange({
              mode: "detail",
              kind: detailKindByRanking[rankingRequest.kind],
              id: item.id,
              title: item.name,
              returnTo: {
                kind: rankingRequest.kind,
                sort: rankingRequest.sort,
              },
            } as DetailRequest);
          }}
          onLoadMore={() => ranking.fetchNextPage()}
          onRetry={() => void ranking.refetch()}
          onFocusRestored={() => setRestoreFocusItemId(null)}
        />
      ) : detailRequest ? (
        <DashboardDetailDrawer
          presentation={presentation}
          pages={detailPages}
          isPending={detail.isPending}
          isFetchingNextPage={detail.isFetchingNextPage}
          error={detail.error ?? detail.authorityError}
          hasNextPage={Boolean(detail.hasNextPage)}
          onLoadMore={() => void detail.fetchNextPage()}
          onRetry={() => void detail.refetch()}
          onBack={() => {
            if ("returnTo" in detailRequest) {
              setRestoreFocusItemId(detailRequest.id);
              onRequestChange({
                mode: "ranking",
                ...detailRequest.returnTo,
                title:
                  user.role === "super" &&
                  detailRequest.returnTo.kind === "stores"
                    ? "Melhores lojas"
                    : rankingTitle(
                        detailRequest.returnTo.kind,
                        detailRequest.returnTo.sort,
                      ),
              } as RankingRequest);
              return;
            }
            onRequestChange(null);
          }}
        />
      ) : localRequest ? (
        <DashboardLocalPanel
          title={localTitle(user.role, localRequest)}
          rows={localRows(user, analytics, localRequest)}
        />
      ) : null}
    </PanelFrame>
  );
}
